import type { LiveWaveformProps } from './const';
import { cn } from '@utils';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { ANIMATION_CONFIG, DEFAULT_CONFIG } from './const';
import {
  calculateProcessingDataScrolling,
  calculateProcessingDataStatic,
  calculateScrollingValueFromFrequency,
  calculateStaticBarsFromFrequency,
  createFadeGradient,
  drawRoundedBar,
} from './helper';
import './index.less';

function releaseInternalAudioResources(stream: MediaStream | null, audioContext: AudioContext | null) {
  stream?.getTracks().forEach(track => track.stop());

  if (audioContext && audioContext.state !== 'closed') {
    void audioContext.close().catch(() => undefined);
  }
}

function LiveWaveform(
  {
    active = false,
    processing = false,
    analyser: externalAnalyser,
    deviceId,
    barWidth = DEFAULT_CONFIG.barWidth,
    barGap = DEFAULT_CONFIG.barGap,
    barRadius = DEFAULT_CONFIG.barRadius,
    barColor,
    fadeEdges = true,
    fadeWidth = DEFAULT_CONFIG.fadeWidth,
    barHeight: baseBarHeight = DEFAULT_CONFIG.barHeight,
    height = DEFAULT_CONFIG.height,
    sensitivity = DEFAULT_CONFIG.sensitivity,
    smoothingTimeConstant = DEFAULT_CONFIG.smoothingTimeConstant,
    fftSize = DEFAULT_CONFIG.fftSize,
    historySize = DEFAULT_CONFIG.historySize,
    updateRate = DEFAULT_CONFIG.updateRate,
    mode = DEFAULT_CONFIG.mode,
    onError,
    onStreamReady,
    onStreamEnd,
    className,
    ...props
  }: LiveWaveformProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<number[]>([]);
  const fadeAnimationRef = useRef<number | null>(null);
  const internalAnalyserRef = useRef<AnalyserNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(externalAnalyser ?? null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number>(0);
  const lastUpdateRef = useRef<number>(0);
  const processingAnimationRef = useRef<number | null>(null);
  const lastActiveDataRef = useRef<number[]>([]);
  const transitionProgressRef = useRef(0);
  const staticBarsRef = useRef<number[]>([]);
  const needsRedrawRef = useRef(true);
  const gradientCacheRef = useRef<CanvasGradient | null>(null);
  const lastWidthRef = useRef(0);

  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement, []);

  const useExternalAnalyser = !!externalAnalyser;
  analyserRef.current = externalAnalyser ?? internalAnalyserRef.current;
  const heightStyle = typeof height === 'number' ? `${height}px` : height;

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(dpr, dpr);
      }

      gradientCacheRef.current = null;
      lastWidthRef.current = rect.width;
      needsRedrawRef.current = true;
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (fadeAnimationRef.current !== null) {
      cancelAnimationFrame(fadeAnimationRef.current);
      fadeAnimationRef.current = null;
    }

    if (processing && !active) {
      let time = 0;
      transitionProgressRef.current = 0;

      const animateProcessing = () => {
        time += ANIMATION_CONFIG.processingSpeed;
        transitionProgressRef.current = Math.min(
          1,
          transitionProgressRef.current + ANIMATION_CONFIG.transitionSpeed,
        );

        const barCount = Math.floor(
          (containerRef.current?.getBoundingClientRect().width || 200) / (barWidth + barGap),
        );
        const processingData =
          mode === 'static'
            ? calculateProcessingDataStatic(
                time,
                barCount,
                transitionProgressRef.current,
                lastActiveDataRef.current,
              )
            : calculateProcessingDataScrolling(
                time,
                barCount,
                transitionProgressRef.current,
                lastActiveDataRef.current,
              );

        if (mode === 'static') {
          staticBarsRef.current = processingData;
        } else {
          historyRef.current = processingData;
        }

        needsRedrawRef.current = true;
        processingAnimationRef.current = requestAnimationFrame(animateProcessing);
      };

      animateProcessing();

      return () => {
        if (processingAnimationRef.current) {
          cancelAnimationFrame(processingAnimationRef.current);
        }
      };
    }

    if (!active && !processing) {
      const hasData =
        mode === 'static' ? staticBarsRef.current.length > 0 : historyRef.current.length > 0;

      if (hasData) {
        let fadeProgress = 0;
        const fadeToIdle = () => {
          fadeProgress += ANIMATION_CONFIG.fadeSpeed;
          if (fadeProgress < 1) {
            if (mode === 'static') {
              staticBarsRef.current = staticBarsRef.current.map(value => value * (1 - fadeProgress));
            } else {
              historyRef.current = historyRef.current.map(value => value * (1 - fadeProgress));
            }
            needsRedrawRef.current = true;
            fadeAnimationRef.current = requestAnimationFrame(fadeToIdle);
          } else if (mode === 'static') {
            staticBarsRef.current = [];
            fadeAnimationRef.current = null;
          } else {
            historyRef.current = [];
            fadeAnimationRef.current = null;
          }
        };
        fadeToIdle();
      }
    }

    return () => {
      if (fadeAnimationRef.current !== null) {
        cancelAnimationFrame(fadeAnimationRef.current);
        fadeAnimationRef.current = null;
      }
    };
  }, [active, barGap, barWidth, mode, processing]);

  useEffect(() => {
    let cancelled = false;

    if (useExternalAnalyser) {
      return () => {
        cancelled = true;
      };
    }

    if (!active) {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        onStreamEnd?.();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        void audioContextRef.current.close().catch(() => undefined);
        audioContextRef.current = null;
      }
      internalAnalyserRef.current = null;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
      return;
    }

    const setupMicrophone = async () => {
      let setupStream: MediaStream | null = null;
      let setupAudioContext: AudioContext | null = null;

      try {
        setupStream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId
            ? {
                deviceId: { exact: deviceId },
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              }
            : {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
              },
        });

        if (cancelled) {
          releaseInternalAudioResources(setupStream, null);
          return;
        }

        streamRef.current = setupStream;
        onStreamReady?.(setupStream);

        const AudioContextConstructor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        setupAudioContext = new AudioContextConstructor();
        audioContextRef.current = setupAudioContext;
        const analyser = setupAudioContext.createAnalyser();
        analyser.fftSize = fftSize;
        analyser.smoothingTimeConstant = smoothingTimeConstant;

        const source = setupAudioContext.createMediaStreamSource(setupStream);
        source.connect(analyser);

        internalAnalyserRef.current = analyser;
        analyserRef.current = analyser;
        historyRef.current = [];
      } catch (error) {
        releaseInternalAudioResources(setupStream, setupAudioContext);

        if (streamRef.current === setupStream) {
          streamRef.current = null;
          onStreamEnd?.();
        }
        if (audioContextRef.current === setupAudioContext) {
          audioContextRef.current = null;
        }
        internalAnalyserRef.current = null;
        if (!cancelled) {
          analyserRef.current = null;
        }

        if (!cancelled) {
          onError?.(error as Error);
        }
      }
    };

    void setupMicrophone();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        releaseInternalAudioResources(streamRef.current, null);
        streamRef.current = null;
        onStreamEnd?.();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        releaseInternalAudioResources(null, audioContextRef.current);
        audioContextRef.current = null;
      }
      internalAnalyserRef.current = null;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
    };
  }, [
    active,
    deviceId,
    fftSize,
    onError,
    onStreamEnd,
    onStreamReady,
    smoothingTimeConstant,
    useExternalAnalyser,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    let rafId: number;

    const animate = (currentTime: number) => {
      const rect = canvas.getBoundingClientRect();

      if (active && currentTime - lastUpdateRef.current > updateRate) {
        lastUpdateRef.current = currentTime;

        if (analyserRef.current) {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(dataArray);

          if (mode === 'static') {
            const barCount = Math.floor(rect.width / (barWidth + barGap));
            const newBars = calculateStaticBarsFromFrequency(dataArray, barCount, sensitivity);
            staticBarsRef.current = newBars;
            lastActiveDataRef.current = newBars;
          } else {
            const value = calculateScrollingValueFromFrequency(dataArray, sensitivity);
            historyRef.current.push(value);
            lastActiveDataRef.current = [...historyRef.current];

            if (historyRef.current.length > historySize) {
              historyRef.current.shift();
            }
          }
          needsRedrawRef.current = true;
        }
      }

      if (!needsRedrawRef.current && !active) {
        rafId = requestAnimationFrame(animate);
        return;
      }

      needsRedrawRef.current = active;
      ctx.clearRect(0, 0, rect.width, rect.height);

      const computedBarColor =
        barColor ||
        (() => {
          const style = getComputedStyle(canvas);
          return style.color || '#000';
        })();
      const step = barWidth + barGap;
      const barCount = Math.floor(rect.width / step);
      const centerY = rect.height / 2;

      if (mode === 'static') {
        const dataToRender = staticBarsRef.current;

        for (let i = 0; i < barCount && i < dataToRender.length; i++) {
          const value = dataToRender[i] || 0.1;
          const x = i * step;
          const barHeight = Math.max(baseBarHeight, value * rect.height * 0.8);
          const y = centerY - barHeight / 2;
          const alpha = 0.4 + value * 0.6;

          drawRoundedBar(ctx, x, y, barWidth, barHeight, barRadius, computedBarColor, alpha);
        }
      } else {
        for (let i = 0; i < barCount && i < historyRef.current.length; i++) {
          const dataIndex = historyRef.current.length - 1 - i;
          const value = historyRef.current[dataIndex] || 0.1;
          const x = rect.width - (i + 1) * step;
          const barHeight = Math.max(baseBarHeight, value * rect.height * 0.8);
          const y = centerY - barHeight / 2;
          const alpha = 0.4 + value * 0.6;

          drawRoundedBar(ctx, x, y, barWidth, barHeight, barRadius, computedBarColor, alpha);
        }
      }

      if (fadeEdges && fadeWidth > 0 && rect.width > 0) {
        if (!gradientCacheRef.current || lastWidthRef.current !== rect.width) {
          gradientCacheRef.current = createFadeGradient(ctx, rect.width, fadeWidth);
          lastWidthRef.current = rect.width;
        }

        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = gradientCacheRef.current;
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.globalCompositeOperation = 'source-over';
      }

      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    active,
    barColor,
    barGap,
    barRadius,
    barWidth,
    baseBarHeight,
    fadeEdges,
    fadeWidth,
    historySize,
    mode,
    processing,
    sensitivity,
    updateRate,
  ]);

  return (
    <div
      className={cn('wg-live-waveform', className)}
      ref={containerRef}
      style={{ height: heightStyle }}
      aria-label={active ? 'Live audio waveform' : processing ? 'Processing audio' : 'Audio waveform idle'}
      role="img"
      {...props}
    >
      {!active && !processing && <div className="wg-live-waveform__idle-line" />}
      <canvas className="wg-live-waveform__canvas" ref={canvasRef} aria-hidden="true" />
    </div>
  );
}

const LiveWaveformComponent = forwardRef<HTMLDivElement, LiveWaveformProps>(LiveWaveform);
LiveWaveformComponent.displayName = 'LiveWaveform';

export default LiveWaveformComponent;
export type { LiveWaveformProps, WaveformMode } from './const';
